







import { UserRequestFilters } from '@/api/requests/users/user-request-filters';
import { PartnerPractice } from '@/shared/partner-practice';
import { User } from '@/shared/user';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const partnerPracticesModule = namespace('partnerPractices');
const userModule = namespace('user');
const usersModule = namespace('users');

@Component
export default class UserSelect extends Vue {
    @partnerPracticesModule.Getter('partnerPracticesForCurrentUser') private partnerPractices!: Array<PartnerPractice>;
    @partnerPracticesModule.Getter('partnerPracticeName') private partnerPracticeName!: (partnerPracticeId: string | null) => string;

    @userModule.Getter('user') private currentUser!: User;

    @usersModule.Getter('legalAdvisors') private legalAdvisors!: Array<User>;
    @usersModule.Getter('appointmentBookers') private appointmentBookers!: Array<User>;
    @usersModule.Getter('interactionAuthors') private interacionAuthors!: Array<User>;
    @usersModule.Getter('taskUsers') private taskUsers!: Array<User>;
    @usersModule.Getter('assignedTaskUsers') private assignedTaskUsers!: Array<User>;
    @usersModule.Getter('completedTaskUsers') private completedTaskUsers!: Array<User>;
    @usersModule.Getter('accountAuthors') private accountAuthors!: Array<User>;
    @usersModule.Getter('assignedCallUsers') private assignedCallUsers!: Array<User>;
    @usersModule.Getter('accountAccessUsers') private accountAccessUsers!: Array<User>;
    @usersModule.Getter('targetUsers') private targetUsers!: Array<User>;

    @Prop({ required: true }) private value!: string | null;
    @Prop({ required: true }) private type!: string;
    @Prop({ required: false, default: null }) private partnerPracticeId!: string | null;
    @Prop({ required: false, default: null }) private datePeriod!: number | null;
    @Prop({ required: false, default: null }) private dateFrom!: string | Date | null;
    @Prop({ required: false, default: null }) private dateTo!: string | Date | null;
    @Prop({ required: false, default: null }) private roleId!: string | null;
    @Prop({ required: false, default: false }) private includeUnassignedOption!: boolean;

    private input: string | null = this.value;

    private get users(): Array<User> {
        switch (this.type) {
            case 'legal advisor':
                return this.legalAdvisors;
            case 'appointment booker':
                return this.appointmentBookers;
            case 'interaction author':
                return this.interacionAuthors;
            case 'task':
                return this.taskUsers;
            case 'assigned task':
                return this.assignedTaskUsers;
            case 'completed task':
                return this.completedTaskUsers;
            case 'account author':
                return this.accountAuthors;
            case 'assigned call':
                return this.assignedCallUsers;
            case 'account access':
                return this.accountAccessUsers;
            case 'target':
                return this.targetUsers;
            default:
                return [];
        }
    }

    private get options(): Array<{}> {
        const options: Array<{ value: string, text: string }> = this.users.map((user) => {
            return {
                value: user.id!,
                text: `${user.firstName.trim()} ${user.lastName.trim()}`.trim(),
            };
        });

        if (this.type === 'assigned task') {
            const assignedTaskOptions = [
                { value: null, text: 'All' },
                { value: this.currentUser.id!, text: 'My Tasks' },
                ...options,
            ];

            if (this.includeUnassignedOption) {
                assignedTaskOptions.splice(1, 0, { value: 'unassigned', text: 'Unassigned' });
            }

            return assignedTaskOptions;
        }

        return [
            { value: null, text: 'All' },
            ...options,
        ];
    }

    @Watch('value')
    private onValueChanged(value: string | null): void {
        this.input = value;
    }

    @Watch('input')
    private onInputChanged(): void {
        this.$emit('input', this.input);
    }

    @Watch('partnerPractices.length')
    private async onPartnerPracticesLengthChangedAsync(): Promise<void> {
        await this.resetOptionsAsync();
    }

    @Watch('partnerPracticeId')
    private async onPartnerPracticeIdChangedAsync(): Promise<void> {
        await this.resetOptionsAsync();
    }

    @Watch('datePeriod')
    private async onDatePeriodChangedAsync(): Promise<void> {
        await this.resetOptionsAsync();
    }

    @Watch('dateFrom')
    private async onDateFromChangedAsync(): Promise<void> {
        await this.resetOptionsAsync();
    }

    @Watch('dateTo')
    private async onDateToChangedAsync(): Promise<void> {
        await this.resetOptionsAsync();
    }

    @Watch('roleId')
    private async onRoleIdChangedAsync(): Promise<void> {
        await this.resetOptionsAsync();
    }

    private async loadUsersAsync(): Promise<void> {
        if (this.partnerPractices.length === 0) { return; }

        if (this.type === 'task') {
            const filters: UserRequestFilters = {
                partnerPracticeId: this.partnerPractices.length === 1 ? this.partnerPractices[0].id! : this.partnerPracticeId,
                datePeriod: this.datePeriod,
                dateFrom: this.dateFrom,
                dateTo: this.dateTo,
                roleId: this.roleId,
            };

            await this.$store.dispatch(`users/loadUsersAsync`, { filters, type: `assigned task` });
            await this.$store.dispatch(`users/loadUsersAsync`, { filters, type: `completed task` });

            return;
        }

        await this.$store.dispatch(`users/loadUsersAsync`, {
            type: this.type,
            filters: {
                partnerPracticeId: this.partnerPractices.length === 1 ? this.partnerPractices[0].id! : this.partnerPracticeId,
                datePeriod: this.datePeriod,
                dateFrom: this.dateFrom,
                dateTo: this.dateTo,
                roleId: this.roleId,
            },
        });
    }

    private async resetOptionsAsync(): Promise<void> {
        await this.loadUsersAsync();
        this.input = null;
    }

    private async mounted(): Promise<void> {
        await this.loadUsersAsync();
    }
}
